import { eventDataFilterParamsAtom, eventsDataCountAtom } from '@/atoms/queries/eventData';
import { EventDataControls } from '@/components/controls/EventsDataControls';
import { DataLockerEventsTable } from '@/components/dataLocker/DataLockerEventsTable';
import { PageTitle } from '@/components/head/PageTitle';
import { DataLockerLayoutWithFilters } from '@/components/layout/dataLocker/DataLockerLayoutWithFilters';
import { VisDisplaySection } from '@/components/section/VisDisplaySection';
import { EventCountInfoBar } from '@/components/visualisations/EventCountInfoBar';
import { PitchVisualisation } from '@/components/visualisations/PitchVisualisation/PitchVisualisation';
import { useAutoResetPagination } from '@/hooks/useAutoResetPagination';
import { useTableColumns } from '@/hooks/useTableColumns';
import { VisDisplayMode } from '@/types/visualisation';
import { useAtomValue, useSetAtom } from 'jotai';
import { eventsUrl } from '@/query/url';
import { useTranslation } from 'react-i18next';
import { ScopeProvider } from 'jotai-scope';
import { tableColumnEntitiesAtom } from '@/atoms/queries/tableColumns';
import { useCsvDownload } from '@/hooks/exporting/useCsvDownload';
import { VisSectionContainer } from '@/components/layout/VisSectionContainer';
import { EVENTS_FILTER_ATOMS, EVENTS_TABLE_COLUMN_ENTITIES } from '@/consts/dataLocker/events';
import { unwrappedEventsDataDefaultColumnsAtom } from '@/atoms/dataLocker/events/table';
import { ScopedTableWithDefaultColumns } from '@/components/tables/ScopedTableWithDefaultColumns';
import { TableId } from '@/types/table';
import { useSetDataLockerDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetDataLockerDebouncedFilterParams';
import {
  displaySettingsLinkAtom,
  eventLinkAtom,
  playerLinkAtom,
  playerPreviewLink,
  routeCurrentPageLink,
  samePageLink,
  videoPreviewPageLink,
} from '@/atoms/navigation';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { FirstTimeModalExplore } from '@/components/modal/FirstTimeModals/FirstTimeModalExplore';
import { FirstTimeModalTypes } from '@/types/userConfigs';
import { useShouldShowHiddenFeature } from '@/hooks/useShouldShowHiddenFeature';
import { dataLockerVisualisationComponents } from '../visComponents';

const CsvDownload = ({ pageTitle }: { pageTitle: string }) => {
  const filterParams = useAtomValue(eventDataFilterParamsAtom);
  useCsvDownload(pageTitle, eventsUrl(filterParams));
  return null;
};

const SetLinkBehaviour = () => {
  const setEventLink = useSetAtom(eventLinkAtom);
  const setPlayerLink = useSetAtom(playerLinkAtom);
  const setDisplaySettingsLink = useSetAtom(displaySettingsLinkAtom);
  const { pathname } = useLocation();

  useEffect(() => {
    setEventLink(videoPreviewPageLink);
    setPlayerLink(playerPreviewLink);
  }, []);

  useEffect(() => {
    setDisplaySettingsLink(pathname.includes('video/preview') ? routeCurrentPageLink : samePageLink);
  }, [pathname]);

  return null;
};

const VisDisplaySectionWithControls = () => {
  useTableColumns(EVENTS_TABLE_COLUMN_ENTITIES);

  return (
    <>
      <EventDataControls defaultDisplayMode={VisDisplayMode.TABLE} />
      <VisDisplaySection
        EventsTableComponent={DataLockerEventsTable}
        visualisationComponent={
          <PitchVisualisation
            visualisations={dataLockerVisualisationComponents}
            footerComponent={<EventCountInfoBar eventCountAtom={eventsDataCountAtom} />}
          />
        }
      />
    </>
  );
};

const VisDisplaySectionWithControlsWrapper = ({ pageTitle }: { pageTitle: string }) => (
  <VisSectionContainer>
    <ScopedTableWithDefaultColumns
      defaultColumnsAtom={unwrappedEventsDataDefaultColumnsAtom}
      tableId={TableId.EVENT_DATA}
    >
      <CsvDownload pageTitle={pageTitle} />
      <SetLinkBehaviour />
      <VisDisplaySectionWithControls />
    </ScopedTableWithDefaultColumns>
  </VisSectionContainer>
);

const AutoResetPagination = () => {
  const filterParams = useAtomValue(eventDataFilterParamsAtom);
  useAutoResetPagination(filterParams);
  return null;
};

export const DebouncedEventsFilterParams = () => {
  useSetDataLockerDebouncedFilterParams(EVENTS_FILTER_ATOMS);
  return null;
};

export const EventDataLockerPage = () => {
  const { t } = useTranslation('entity');
  const eventDataLockerPageTitle = t('event.other');

  return (
    <>
      <AutoResetPagination />
      <DebouncedEventsFilterParams />
      <ScopeProvider atoms={[tableColumnEntitiesAtom, eventLinkAtom, playerLinkAtom]}>
        <PageTitle>{eventDataLockerPageTitle}</PageTitle>
        {useShouldShowHiddenFeature() && (
          <FirstTimeModalExplore entity="event" modalType={FirstTimeModalTypes.EVENT_EXPLORE} />
        )}
        <DataLockerLayoutWithFilters>
          <VisDisplaySectionWithControlsWrapper pageTitle={eventDataLockerPageTitle} />
        </DataLockerLayoutWithFilters>
      </ScopeProvider>
    </>
  );
};
